import { MessageStates } from 'containers/MessageBox';
import { call, put } from 'redux-saga/effects';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { checkResponse } from 'store/utils';
import {
  setDriveScannerStatus,
  setFilesUploadHistory,
  setGoogleAccessToken,
} from '../actions';
import {
  disconnectFolderRequest,
  getDriveScannerStatus,
  getFilesUploadHistory,
  registerDriveFolder,
  saveGoogleToken,
} from '../api';
import { renderErrorMessage } from 'utils/render-error-message';
import { logoutRequest } from 'store/auth/actions';
import i18n from 'i18next';

export function* registerDriveFolderSaga(action: any) {
  try {
    const { folderId, connectionId, type, driveId, syncExistingFiles } =
      action.payload;
    const response: any = yield call(
      registerDriveFolder,
      folderId,
      connectionId,
      type,
      driveId,
      syncExistingFiles
    );

    const err = checkResponse(response?.responseMessage);
    if (err) throw err;

    const provider = type === 'google' ? 'Google Drive' : 'One Drive';
    const message: MessageData = {
      title: i18n.t('common.success-exclamation'),
      description: i18n.t('notifications.folder-connected-successfully', {
        provider,
      }),
      type: MessageStates.SUCCESS,
    };

    /** Keep IDs from client + API so "open in Drive" links work before status refetch. */
    const status: Record<string, unknown> = {
      connected: true,
      folderName: response?.data?.folderName,
      drivePath: response?.data?.drivePath,
      driveUrl: response?.data?.driveUrl,
      folderId: response?.data?.folderId ?? folderId,
    };
    const resolvedDriveId = response?.data?.driveId ?? driveId;
    if (resolvedDriveId != null && resolvedDriveId !== '') {
      status.driveId = resolvedDriveId;
    }

    if (type === 'google') {
      yield put(setDriveScannerStatus(status, undefined));
    } else {
      yield put(setDriveScannerStatus(undefined, status));
    }

    yield put(turnMessageOn(message));
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
  }
}

export function* saveGoogleTokenSaga(action: any) {
  try {
    const { code, connectionId } = action.payload;

    const response: any = yield call(saveGoogleToken, code, connectionId);

    const err = checkResponse(response?.responseMessage);
    if (err) throw err;

    const accessToken = response?.data?.accessToken;
    yield put(setGoogleAccessToken(accessToken));
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
  }
}

export function* getDriveScannerStatusSaga(action: any) {
  try {
    const { connectionId } = action.payload;
    const response: any = yield call(getDriveScannerStatus, connectionId);

    const err = checkResponse(response?.responseMessage);
    if (err) throw err;

    const googleStatus = response?.data?.status?.googleDrive;
    const oneDriveStatus = response?.data?.status?.oneDrive;

    yield put(setDriveScannerStatus(googleStatus, oneDriveStatus));
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
  }
}

export function* getFilesUploadHistorySaga(action: any) {
  try {
    const { connectionId, type } = action.payload;
    const response: any = yield call(getFilesUploadHistory, connectionId, type);

    const err = checkResponse(response?.responseMessage);
    if (err) throw err;

    yield put(setFilesUploadHistory(response?.data));
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
  }
}

export function* disconnectFolderSaga(action: any) {
  try {
    const { type, connectionId } = action.payload;
    const response: any = yield call(disconnectFolderRequest, connectionId, type);

    const err = checkResponse(response?.responseMessage);
    if (err) throw err;

    const message: MessageData = {
      title: i18n.t('common.success-exclamation'),
      description: i18n.t('notifications.folder-unsubscribed-successfully'),
      type: MessageStates.SUCCESS,
    };

    yield put(turnMessageOn(message));
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
  }
}
